import { get, invertBy, omit, sumBy } from "lodash";
import { isContractsSymbol } from "@shared/utils/misc";
import {
  PORTFOLIO_DATA_CLIENTS_HEADER,
  PORTFOLIO_DATA_CLIENT_HEADER,
  PORTFOLIO_DATA_EXCHANGE_HEADER,
  PORTFOLIO_DATA_EXCHANGES_HEADER,
} from "@shared/features/portfolio/constants";
import {
  ClientData,
  ClientDependency,
  ConfigurationExchangeInfo,
  ConsolidatedAccountData,
  ConsolidatedCoinData,
  ConsolidatedExchangeData,
  DisplayCurrency,
  FixMeLater,
  GetAccountExtraInfoGeneralResponse,
  GetPortfolioDataResponse,
  PortfolioData,
  ProcessedCoinData,
  Status,
  TradeExtraInfoGeneralMapData,
} from "@shared/types";
import {
  formatTimeSeriesChartData,
  preprocessPieChartData,
} from "@shared/utils/charts";
import { round, toFixed } from "@shared/utils/numbers";
import {
  DISPLAY_CURRENCIES,
  EXCHANGES_UNIQUE_IDS,
} from "@shared/common/constants";
import { AppThemeType } from "@shared/common/Themes/AppTheme";

const consolidateArrayFields = (
  mainEntry:
    | ConsolidatedCoinData
    | ConsolidatedAccountData
    | ConsolidatedExchangeData,
  entryToConsolidate: ProcessedCoinData
) => {
  [
    [PORTFOLIO_DATA_EXCHANGES_HEADER, PORTFOLIO_DATA_EXCHANGE_HEADER],
    [PORTFOLIO_DATA_CLIENTS_HEADER, PORTFOLIO_DATA_CLIENT_HEADER],
  ].forEach(([mainFieldName, consolidateFieldName]) => {
    if (mainFieldName in mainEntry) {
      const mainEntryArray = (mainEntry as any)[mainFieldName] as string[];
      const itemToConsolidate = entryToConsolidate[
        consolidateFieldName as keyof ProcessedCoinData
      ] as string;
      if (!mainEntryArray.includes(itemToConsolidate)) {
        mainEntryArray.push(itemToConsolidate);
      }
    }
  });
};

const consolidateSumFields = (
  mainEntry:
    | ConsolidatedCoinData
    | ConsolidatedAccountData
    | ConsolidatedExchangeData,
  entryToConsolidate: ProcessedCoinData | ConsolidatedAccountData
) => {
  (
    [
      "quantity",
      "available_quantity",
      "total_in_btc",
      "total_in_usd",
      "available_in_btc",
      "available_in_usd",
      "total_ratio",
      "exchange_ratio",
    ] as const
  ).forEach((fieldName) => {
    const value =
      fieldName in entryToConsolidate
        ? // @ts-ignore
          entryToConsolidate[fieldName]
        : undefined;
    if (fieldName in mainEntry && value) {
      (mainEntry as any)[fieldName] += value;
    }
  });
};

const consolidatePortfolioItems = (
  mainEntry:
    | ConsolidatedCoinData
    | ConsolidatedAccountData
    | ConsolidatedExchangeData,
  coinData: ProcessedCoinData | ConsolidatedAccountData,
  clientUuidToProcessedCoins: { [p: string]: Set<string> },
  groupByAccount: boolean
) => {
  const isSymbolData = "symbol" in coinData;
  if (isSymbolData) consolidateArrayFields(mainEntry, coinData);
  const processedCoins = clientUuidToProcessedCoins[coinData.client_uuid];
  const coinKey = getCoinKey(coinData);
  const shouldProcessCoin = isSymbolData && !processedCoins?.has(coinKey);
  if (groupByAccount || shouldProcessCoin) {
    consolidateSumFields(mainEntry, coinData);
  }
  if ("is_sellable" in coinData) {
    (mainEntry as ConsolidatedCoinData).is_sellable =
      (mainEntry as ConsolidatedCoinData).is_sellable || coinData.is_sellable;
  }
  if ("accountsSellData" in mainEntry) {
    mainEntry.accountsSellData.push({
      account: coinData.client,
      exchange: coinData.exchange,
      accountId: coinData.client_id,
      sellable: (coinData as ProcessedCoinData).is_sellable,
    });
  }
};

const getBaseConsolidatedCoinData = (
  coinData: ProcessedCoinData
): ConsolidatedCoinData => {
  let copiedCoinData: Partial<ConsolidatedCoinData> = { ...coinData };

  const exchange = coinData[PORTFOLIO_DATA_EXCHANGE_HEADER];
  const account = coinData[PORTFOLIO_DATA_CLIENT_HEADER];
  copiedCoinData.exchanges = [exchange];
  copiedCoinData.clients = [account];
  copiedCoinData.accountsSellData = [
    {
      exchange,
      account,
      accountId: coinData.client_id,
      sellable: coinData.is_sellable,
    },
  ];
  return omit(copiedCoinData, ["exchange", "client"]) as ConsolidatedCoinData;
};

const getCoinKey = (
  coinData:
    | ConsolidatedCoinData
    | ConsolidatedAccountData
    | ConsolidatedExchangeData
) => {
  const isSymbolData = "symbol" in coinData;
  let entryKey = isSymbolData ? coinData.symbol : "";
  if (entryKey && "is_short" in coinData) {
    return `${coinData.client_uuid}_${entryKey}_${
      coinData.is_short ? "short" : "long"
    }_${coinData.margin_type}`;
  }
  return entryKey;
};

export const getConsolidatedPortfolioData = (
  data: (ProcessedCoinData | ConsolidatedAccountData)[],
  groupByAccount = false,
  groupByExchange = false
): (
  | ConsolidatedCoinData
  | ConsolidatedAccountData
  | ConsolidatedExchangeData
)[] => {
  const clientUuidToProcessedCoins: { [key: string]: Set<string> } = {};
  const entryKeyToData: {
    [key: string]:
      | ConsolidatedCoinData
      | ConsolidatedAccountData
      | ConsolidatedExchangeData;
  } = {};
  const consolidatedData: (
    | ConsolidatedCoinData
    | ConsolidatedAccountData
    | ConsolidatedExchangeData
  )[] = [];
  data.forEach((coinData) => {
    const isSymbolData = "symbol" in coinData;
    const clientUuid = coinData.client_uuid;
    let entryKey: string | undefined;
    if (groupByAccount) entryKey = coinData.client;
    else if (groupByExchange) entryKey = coinData.exchange;
    else entryKey = getCoinKey(coinData);
    const previousEntry = entryKey && entryKeyToData[entryKey];
    const shouldConsolidate = groupByAccount || groupByExchange || isSymbolData;
    if (previousEntry && shouldConsolidate) {
      consolidatePortfolioItems(
        previousEntry,
        coinData,
        clientUuidToProcessedCoins,
        groupByAccount
      );
    } else if (entryKey) {
      const consolidatedItem =
        groupByAccount || groupByExchange || !isSymbolData
          ? ({ ...coinData } as ConsolidatedAccountData)
          : getBaseConsolidatedCoinData(coinData);
      entryKeyToData[entryKey] = consolidatedItem;
      consolidatedData.push(consolidatedItem);
    }

    if (isSymbolData) {
      if (clientUuid in clientUuidToProcessedCoins) {
        clientUuidToProcessedCoins[clientUuid].add(entryKey);
      } else {
        clientUuidToProcessedCoins[clientUuid] = new Set([entryKey]);
      }
    }
  });
  return consolidatedData;
};

export const updateCoinRatios = <T extends { total_in_usd: number }>(
  data: T[]
) => {
  const sum = sumBy(data, (coin) =>
    coin.total_in_usd > 0 ? coin.total_in_usd : 0
  );
  return data.map((coin) => ({
    ...coin,
    total_ratio:
      sum && coin.total_in_usd > 0 ? round(coin.total_in_usd / sum, 4) : 0,
  }));
};

const getProcessedCoinCardsData = (
  portfolioData: PortfolioData,
  exchange: string,
  client: string,
  indexToHeaderMapping: { [p: number]: string }
): ProcessedCoinData[] => {
  const clientData = portfolioData[exchange]?.[client];
  if (!clientData) return [];

  const clientCoinCards = clientData.coin_cards || [];
  return clientCoinCards.map((coinData) => {
    const processedCoinData: Partial<ProcessedCoinData> = {
      exchange,
      client,
      client_uuid: clientData.uuid,
      client_id: clientData.id,
    };
    coinData.forEach((value, index) => {
      const header = indexToHeaderMapping[index];
      (processedCoinData as any)[header] = value;
    });
    if (isContractsSymbol(processedCoinData["symbol"])) {
      processedCoinData.is_short = processedCoinData.quantity! < 0;
    }
    return processedCoinData as ProcessedCoinData;
  });
};

export const preprocessPortfolioData = (
  portfolioData: GetPortfolioDataResponse | undefined,
  exchangesToClientsMapping: { [key: string]: string[] | undefined }
): ProcessedCoinData[] => {
  if (!portfolioData || !portfolioData.data) return [];
  const headers = portfolioData.headers || [];
  const indexToHeaderMapping = headers.reduce(
    (acc: { [key: number]: string }, headerKey) => {
      acc[headers.indexOf(headerKey)] = headerKey;
      return acc;
    },
    {}
  );

  const coinsData: ProcessedCoinData[] = [];
  Object.entries(exchangesToClientsMapping).forEach(([exchange, clients]) => {
    clients?.forEach((client) => {
      coinsData.push(
        ...getProcessedCoinCardsData(
          portfolioData.data,
          exchange,
          client,
          indexToHeaderMapping
        )
      );
    });
  });
  return coinsData;
};

export const filterExtraInfoMap = (
  extraInfoMap: Record<string, string | number | null>
) => {
  const filteredInfoMap: Record<string, string | number> = {};
  const mapKeys = Object.keys(
    extraInfoMap
  ) as (keyof TradeExtraInfoGeneralMapData)[];
  return mapKeys.reduce((acc: FixMeLater, key) => {
    const value = extraInfoMap[key];
    if (["number", "string"].includes(typeof value)) {
      acc[key] = value;
    }
    return acc;
  }, filteredInfoMap);
};

export const processAccountExtraInfoGeneralGraphData = (
  data: GetAccountExtraInfoGeneralResponse | undefined,
  time_range: string,
  displayCurrency: string
) => {
  const chartData = data?.data?.graph_data?.[time_range] || [];
  const precisionConfig = data?.precision;
  const yValueIndex = displayCurrency === DISPLAY_CURRENCIES.BTC ? 2 : 1;
  const precision = get(precisionConfig, ["balance", displayCurrency], 2);
  return formatTimeSeriesChartData(
    chartData,
    yValueIndex,
    true,
    null,
    precision
  );
};

export const getPricePrecision = (
  price: number,
  displayCurrency: DisplayCurrency
) => {
  let precision = displayCurrency === DISPLAY_CURRENCIES.USD ? 2 : 5;
  if (price !== 0) {
    let priceWithPrecision = toFixed(price, precision);
    while (priceWithPrecision === 0) {
      precision++;
      priceWithPrecision = toFixed(price, precision);
    }
  }
  return precision;
};

export const getCoinsOrContractsData = <
  Data extends ProcessedCoinData | ConsolidatedCoinData
>(
  data: Data[],
  isContracts?: boolean
) => {
  return data.filter((coinData) =>
    isContracts
      ? isContractsSymbol(coinData.symbol)
      : !isContractsSymbol(coinData.symbol)
  );
};

export const getPieChartData = <T>(
  themeContext: AppThemeType,
  nameHeader: keyof T,
  data: T[],
  valueHeaderPath: string | string[]
) => {
  const pieChartData = data.map((itemData) => ({
    name: itemData[nameHeader] as unknown as string,
    value: get(itemData, valueHeaderPath, 0),
  }));
  return preprocessPieChartData(
    pieChartData,
    themeContext.colors.pieChartColors
  );
};

export const dedupeAccountsData = <
  T extends { exchange: string } & ({ client_id: number } | { id: number })
>(
  accountsData: T[],
  clientDependency?: ClientDependency
) => {
  const getAccountId = (account: T) =>
    "client_id" in account ? account.client_id : account.id;
  const clientIds = new Set(
    accountsData.map((accountData) => getAccountId(accountData))
  );
  return accountsData.filter((accountData) => {
    const accountId = getAccountId(accountData);
    const dependencyAccountIds = clientDependency?.[accountId];
    return (
      !dependencyAccountIds ||
      dependencyAccountIds.every(
        (dependencyAccountId) => !clientIds.has(dependencyAccountId)
      )
    );
  });
};

export const getCoinCardKey = (coinData: ConsolidatedCoinData) => {
  return `${coinData.symbol}_${coinData.is_short ? "short" : "long"}_${
    coinData.leverage ?? 1
  }_${coinData.margin_type}_${coinData.client_id}`;
};

export const getRawFiltersFromClientsList = (data?: ClientData[]) => {
  const accountToExchange = data?.reduce(
    (acc: Record<string, string>, account) => {
      acc[account.client_name] = account.exchange;
      return acc;
    },
    {}
  );
  const accountToId = data?.reduce((acc: Record<string, number>, account) => {
    acc[account.client_name] = account.client_id;
    return acc;
  }, {});
  const exchangeToAccounts = invertBy(accountToExchange);
  return {
    filters: {
      values: {
        Exchanges: exchangeToAccounts,
        Status: Object.values(Status),
      },
      constants: {
        Accounts: accountToId,
        Exchanges: EXCHANGES_UNIQUE_IDS,
      },
    },
  };
};

export const getAccountFullName = (
  name: string,
  subExchange: ConfigurationExchangeInfo
) => {
  if (subExchange.display_suffix)
    return name.concat(" - ", subExchange.display_suffix);
  return name;
};
